/**
 * Rol del usuario actual en Operaciones (Fase R). Lee tenant_ops_members; si no
 * hay asignación, cae a 'admin' (no rompe a nadie). El founder asigna roles
 * restringidos (logística/contabilidad) desde la Configuración de Operaciones.
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@smarty/shared';
import { supabase } from '@/lib/supabase';
import { capsDelMiembro, opsCaps, type OpsRole, type OpsCaps } from '@/lib/operations/orderMath';
import { useAlcanceDelPedido } from '@/components/operations/OrderProvider';
import { useSoyVendedorDelDrop } from '@/hooks/useSoyVendedorDelDrop';

/** Lo que se puede hacer con un pedido que uno ve pero no es quien lo opera. */
const SOLO_LECTURA: OpsCaps = { viewPrices: true, viewCost: false, editOrder: false, collect: false, logistics: false, billing: false };

export interface UseOpsRoleResult {
  loading: boolean;
  role: OpsRole;
  caps: OpsCaps;
  /** Si puede pagar con la billetera del drop. El dueño y el gerente siempre (20260929150000). */
  puedePagar: boolean;
  /**
   * Si puede confirmar ventas (y desconfirmarlas o anular una confirmada). Sin él, el vendedor guarda la venta y
   * la confirma el dueño o el gerente. La base lo exige sola (20261014120000); esto sólo evita ofrecer el botón.
   */
  puedeConfirmar: boolean;
  /** «suyas»: ve sólo las ventas que carga; «todas»: las del negocio. La base lo aplica sola. */
  veVentas: 'suyas' | 'todas';
}

interface FilaDeRol { ops_role: OpsRole; puede_pagar: boolean | null; puede_confirmar?: boolean | null; ve_ventas: 'suyas' | 'todas' | null; ve_costos?: boolean | null }

export function useOpsRole(): UseOpsRoleResult {
  const { tenantId, user, isOwnerOrManager } = useAuth();
  const vendedorDelDrop = useSoyVendedorDelDrop();
  const uid = (user as { id?: string } | null)?.id;

  /**
   * 🚨 Antes esto era `useEffect` + `useState`, y lo usan QUINCE pantallas. Cada montaje de cada una
   * disparaba su propia consulta por el rol — el mismo dato, una y otra vez, en cada navegación.
   *
   * Con la caché compartida, la primera pantalla lo pide y las otras catorce lo leen de memoria: la
   * `queryKey` es la misma, así que TanStack Query deduplica las consultas simultáneas y reutiliza el
   * resultado. Es exactamente para lo que existe esta capa.
   *
   * Dato de referencia: cambia cuando el founder reasigna un rol, o sea casi nunca. El `staleTime`
   * global de 30 s más la revalidación al volver a la pestaña alcanzan de sobra.
   */
  const { data, isPending } = useQuery({
    queryKey: ['ops-role', 'con-permisos', tenantId, uid], // la forma cambió (29-sep): antes guardaba sólo el rol
    enabled: !!tenantId && !!uid,
    queryFn: async (): Promise<FilaDeRol | null> => {
      const { data: fila } = await supabase
        .from('tenant_ops_members')
        .select('ops_role, puede_pagar, puede_confirmar, ve_ventas, ve_costos')
        .eq('tenant_id', tenantId!)
        .eq('user_id', uid!)
        .maybeSingle();
      // Sin asignación explícita: 'admin'. No romper a nadie es más importante que ser estricto —
      // el founder asigna roles restringidos a propósito, la ausencia no significa "sin permisos".
      return (fila as FilaDeRol | null) ?? null;
    },
  });

  /*
   * 🪤 EL VENDEDOR DE UN DROP SIN FILA NO ES ADMINISTRADOR. Para las tiendas, «sin fila» sigue siendo
   *    'admin' (arriba). Pero un empleado de un drop vende: si le faltara la fila, 'admin' le
   *    mostraría los costos que el dueño quiere esconder. Sin fila, es de ventas.
   */
  const role: OpsRole = data?.ops_role ?? (vendedorDelDrop ? 'ventas' : 'admin');
  const administra = isOwnerOrManager();
  const puedePagar = administra || data?.puede_pagar !== false;
  const puedeConfirmar = administra || data?.puede_confirmar !== false;
  const veVentas = !administra && data?.ve_ventas === 'suyas' ? 'suyas' : 'todas';
  // 🪤 `loading` es false cuando no hay a quién consultar (sin sesión o sin tenant): con `enabled`
  // en false, `isPending` se queda en true PARA SIEMPRE y las pantallas quedarían cargando eternamente.
  /*
   * ⭐ DENTRO DE UN PEDIDO, EL ROL NO ALCANZA: TAMBIÉN IMPORTA DE QUIÉN ES. Un administrador de Demo
   *    que abre el encargo de Olimpo que despacha lo ve, pero no lo edita, no cobra y no factura. La
   *    regla vive acá, en el único lugar de donde las quince pantallas del pedido sacan sus permisos.
   */
  const alcance = useAlcanceDelPedido();
  // «Ve costos y pagos» (20261022150000): el permiso del vendedor, que el dueño elige en Equipo.
  const caps = alcance?.soloLectura ? { ...SOLO_LECTURA, viewPrices: opsCaps(role).viewPrices } : capsDelMiembro(role, data?.ve_costos);
  return { loading: !!tenantId && !!uid && isPending, role, caps, puedePagar, puedeConfirmar, veVentas };
}
